import type { CalendarGridEvent, LaidOutCalendarEvent } from '@/types/calendar/grid'
import type { CalendarViewId } from '@/constants/calendar/views'
import {
  addCalendarDays,
  sameCalendarDay,
  startOfWeekMonday,
  toIsoDate
} from '@/utils/calendar/date'
import { layoutDayEvents } from '@/utils/calendar/event-layout'
import { computed, type Ref } from 'vue'

export interface CalendarTimeGridDay {
  abbr: string
  day: number
  date: Date
  today: boolean
}

/**
 * Day/week grid state for the prototype time-grid layout.
 */
export function useCalendarTimeGrid(
  selectedDate: Ref<Date>,
  activeView: Ref<CalendarViewId>,
  eventsByDate: Ref<Map<string, CalendarGridEvent[]>>,
  weekdayAbbrs: Ref<string[]>
) {
  const weekStart = computed(() => startOfWeekMonday(selectedDate.value))

  const weekDays = computed<CalendarTimeGridDay[]>(() =>
    weekdayAbbrs.value.map((abbr, i) => {
      const d = addCalendarDays(weekStart.value, i)
      return {
        abbr,
        day: d.getDate(),
        date: d,
        today: sameCalendarDay(d, selectedDate.value)
      }
    })
  )

  const visibleDays = computed<CalendarTimeGridDay[]>(() =>
    activeView.value === 'day'
      ? [weekDays.value.find((d) => d.today) ?? weekDays.value[0]!]
      : weekDays.value
  )

  const visibleDayCols = computed(() =>
    activeView.value === 'day' ? 'grid-cols-1' : 'grid-cols-7'
  )

  const eventsForDay = computed<LaidOutCalendarEvent[]>(() =>
    layoutDayEvents(eventsByDate.value.get(toIsoDate(selectedDate.value)) ?? [])
  )

  const eventsForWeekday = (dIdx: number): LaidOutCalendarEvent[] => {
    const d = addCalendarDays(weekStart.value, dIdx)
    return layoutDayEvents(eventsByDate.value.get(toIsoDate(d)) ?? [])
  }

  const isDayEmpty = computed(
    () => (eventsByDate.value.get(toIsoDate(selectedDate.value)) ?? []).length === 0
  )

  const isWeekEmpty = computed(() => {
    for (let i = 0; i < 7; i++) {
      const d = addCalendarDays(weekStart.value, i)
      if ((eventsByDate.value.get(toIsoDate(d)) ?? []).length > 0) {
        return false
      }
    }
    return true
  })

  const isWeekend = (idx: number): boolean => idx >= 5

  return {
    weekDays,
    visibleDays,
    visibleDayCols,
    eventsForDay,
    eventsForWeekday,
    isDayEmpty,
    isWeekEmpty,
    isWeekend
  }
}
